// front-end/src/components/course-manager/course-manager.js

import {apiDelete, apiGet, apiPatch, apiPost} from '../../utils/api';
import {getAuthToken} from '../auth';
import {showErrorNotification, showSuccessNotification} from '../../utils/notifications';
import {openLinkPageModal} from '../diagnostic-manager/link-page-modal';
import {config} from '../../config/app-config';

export class CourseManager {
    constructor() {
        this.init();
    }

    init() {
        console.log('CourseManager initialized');
        document.addEventListener('blockPublished', this.handleBlockPublished.bind(this));
        document.addEventListener('blockAdded', this.handleBlockAdded.bind(this));
        this.processExistingCourseBlocks();
    }

    handleBlockAdded(event) {
        const { blockElement } = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');
        if (blockName && blockName.startsWith('course-card-') && getAuthToken()) {
            this.addLinkButtonToBlock(blockElement);
        }
    }

    // Проверяем, должны ли показываться статусы
    shouldShowStatusControls() {
        return window.location.pathname.match(/courses(\.html)?$/);
    }

    handleBlockPublished(event) {
        const {blockElement, blockId, blockData} = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');

        if (blockName && blockName.startsWith('course-card-')) {
            console.log('Course block published:', blockName);
            this.activateCourseLink(blockElement, blockName);

            const pendingId = blockElement.getAttribute('data-pending-link-id');
            if (pendingId) {
                this._applyPendingLink(blockElement, blockId, pendingId);
            } else {
                this.createCourseEntity(blockElement, blockId, blockData);
            }
        }
    }

    async _applyPendingLink(blockElement, blockId, entityId) {
        try {
            const result = await apiPatch(`${config.api.paths.coursesLinkBlock}/${entityId}/block`, { block_id: parseInt(blockId, 10) });
            if (result?.id) {
                blockElement.setAttribute('data-course-id', result.id);
                blockElement.setAttribute('data-course-status', result.status || 'draft');
                blockElement.setAttribute('data-course-slug', result.slug);
                blockElement.removeAttribute('data-pending-link-id');
                blockElement.removeAttribute('data-pending-link-slug');
                this.activateCourseLink(blockElement, result.slug);
            }
        } catch (err) {
            console.error('Error applying pending course link:', err);
        }
    }

    activateCourseLink(blockElement, blockName) {
        const courseLink = blockElement.querySelector('a.news-link-to-feed-container');
        if (courseLink) {
            const dynamicUrl = `/course/${blockName}`;
            courseLink.href = dynamicUrl;
            courseLink.setAttribute('data-page', blockName);
            courseLink.classList.remove('inactive');
            courseLink.classList.add('active');

            console.log(`Course link activated for ${blockName}: ${dynamicUrl}`);
        }
    }

    async createCourseEntity(blockElement, blockId, blockData) {
        const token = getAuthToken();
        if (!token) return;

        try {
            const blockName = blockData.data_block_name;

            // Создаем slug для курса из имени блока
            const courseSlug = blockName;

            const courseData = {
                title: blockName.replace('course-card-', '').replace(/-/g, ' ').toUpperCase(),
                slug: courseSlug,
                preview_text: 'Додайте опис курсу через редактор контенту',
                page_title: `Course - ${blockName}`,
                page_slug: `course-${Date.now()}`,
                block_data_name: blockName  // Только имя блока
            };

            console.log('Creating course entity with data:', courseData);

            let result = await apiPost('/api/courses', courseData);
            if (!result?.id) {
                const existing = await apiGet(`${config.api.paths.coursesBySlug}/${courseSlug}`, { auth: false }).catch(() => null);
                if (existing?.id && blockId) {
                    result = await apiPatch(
                        `${config.api.paths.coursesLinkBlock}/${existing.id}/block`,
                        { block_id: parseInt(blockId, 10) }
                    ).catch(() => null);
                }
            }

            if (result && result.id) {
                // Update block element with course info
                blockElement.setAttribute('data-course-id', result.id);
                blockElement.setAttribute('data-course-status', result.status || 'draft');
                blockElement.setAttribute('data-course-slug', result.slug);

                // Add status indicator - ТОЛЬКО на courses.html
                if (this.shouldShowStatusControls()) {
                    this.updateBlockStatusIndicator(blockElement, result.status || 'draft');
                }

                // Add status controls for authenticated users - ТОЛЬКО на courses.html
                if (getAuthToken() && this.shouldShowStatusControls()) {
                    this.addStatusControlsToBlock(blockElement, result.id);
                }

                this.addLinkButtonToBlock(blockElement);
                console.log('Course entity created successfully:', result);
                console.log(`Block ${blockName} now linked to course ID: ${result.id}`);
                showSuccessNotification('Курс створено в статусі чернетки');
            }

        } catch (error) {
            console.error('Error creating course entity:', error);
            showErrorNotification('Помилка створення курсу');
        }
    }

    addStatusControlsToBlock(blockElement, courseId) {
        // НОВОЕ: Проверяем, должны ли показываться контролы
        if (!this.shouldShowStatusControls()) {
            return;
        }

        // Удаляем существующие контролы если есть
        const existingControls = blockElement.querySelector('.course-status-controls');
        if (existingControls) {
            existingControls.remove();
        }

        const currentStatus = blockElement.getAttribute('data-course-status') || 'draft';

        const controlsContainer = document.createElement('div');
        controlsContainer.className = 'course-status-controls';
        controlsContainer.innerHTML = `
            <div class="status-controls-inner">
                <span class="current-status">Статус: ${currentStatus.toUpperCase()}</span>
                <button class="toggle-status-btn" data-course-id="${courseId}" data-current-status="${currentStatus}">
                    ${currentStatus === 'draft' ? 'Опублікувати' : 'Зняти з публікації'}
                </button>
            </div>
        `;

        blockElement.appendChild(controlsContainer);

        // Add event listener for status toggle
        const toggleBtn = controlsContainer.querySelector('.toggle-status-btn');
        toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleCourseStatus(blockElement, courseId, currentStatus);
        });

        console.log(`Status controls added to block for course ID: ${courseId}`);
    }

    async toggleCourseStatus(blockElement, courseId, currentStatus) {
        const token = getAuthToken();
        if (!token) {
            showErrorNotification('Потрібна авторизація');
            return;
        }

        try {
            const newStatus = currentStatus === 'draft' ? 'published' : 'draft';

            console.log(`Toggling course status from ${currentStatus} to ${newStatus}`);

            // Call the toggle publication endpoint
            const result = await apiPost(`/api/courses/${courseId}/publish`);

            if (result) {
                // Update block attributes
                blockElement.setAttribute('data-course-status', result.status);

                // Update status indicator
                this.updateBlockStatusIndicator(blockElement, result.status);

                // Update controls
                this.updateStatusControls(blockElement, result.status);

                // Update link status
                const courseLink = blockElement.querySelector('a.news-link-to-feed-container');
                if (courseLink) {
                    if (result.status === 'published') {
                        courseLink.classList.remove('inactive');
                        courseLink.classList.add('active');
                        const courseSlug = blockElement.getAttribute('data-course-slug');
                        courseLink.href = `/course/${courseSlug}`;
                    } else {
                        courseLink.classList.remove('active');
                        courseLink.classList.add('inactive');
                        courseLink.href = '#';
                    }
                }

                const statusText = result.status === 'published' ? 'опублікований' : 'знятий з публікації';
                showSuccessNotification(`Курс ${statusText} успішно`);

                console.log('Status updated successfully:', result);
            }

        } catch (error) {
            console.error('Error toggling course status:', error);
            showErrorNotification('Помилка оновлення статусу курсу');
        }
    }

    updateBlockStatusIndicator(blockElement, status) {
        // ФИКС: Проверяем авторизацию перед показом индикатора
        if (!getAuthToken()) {
            // Для неавторизованных удаляем индикатор
            const existingIndicator = blockElement.querySelector('.course-status-indicator');
            if (existingIndicator) {
                existingIndicator.remove();
            }
            return;
        }

        // НОВОЕ: Проверяем, должны ли показываться индикаторы
        if (!this.shouldShowStatusControls()) {
            return;
        }

        let indicator = blockElement.querySelector('.course-status-indicator');

        if (!indicator) {
            indicator = document.createElement('div');
            indicator.className = 'course-status-indicator';
            blockElement.appendChild(indicator);
        }

        indicator.className = `course-status-indicator status-${status}`;
        indicator.innerHTML = `<span class="status-text">${status.toUpperCase()}</span>`;
    }

    updateStatusControls(blockElement, status) {
        const controls = blockElement.querySelector('.course-status-controls');
        if (controls) {
            const statusSpan = controls.querySelector('.current-status');
            const toggleBtn = controls.querySelector('.toggle-status-btn');

            if (statusSpan) {
                statusSpan.textContent = `Статус: ${status.toUpperCase()}`;
            }

            if (toggleBtn) {
                toggleBtn.textContent = status === 'draft' ? 'Опублікувати' : 'Зняти з публікації';
                toggleBtn.setAttribute('data-current-status', status);
            }
        }
    }

    addLinkButtonToBlock(blockElement) {
        if (!getAuthToken()) return;
        if (blockElement.querySelector('.course-link-btn')) return;

        const btn = document.createElement('button');
        btn.className = 'course-link-btn block-control-btn';
        btn.title = 'Прив\'язати сторінку';
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
            <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z"/>
            <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243z"/>
        </svg>`;
        Object.assign(btn.style, {
            position: 'absolute', top: '10px', right: '70px',
            background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '4px',
            color: 'white', cursor: 'pointer', padding: '4px', zIndex: '10',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        });

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const blockId = blockElement.getAttribute('data-block-id') || null;
            const currentSlug = blockElement.getAttribute('data-course-slug') || blockElement.getAttribute('data-pending-link-slug') || null;
            openLinkPageModal(blockId, currentSlug, (result) => {
                if (blockId) {
                    if (result.slug) {
                        blockElement.setAttribute('data-course-id', result.id);
                        blockElement.setAttribute('data-course-slug', result.slug);
                        this.activateCourseLink(blockElement, result.slug);
                    } else {
                        blockElement.removeAttribute('data-course-slug');
                        this.activateCourseLink(blockElement, null);
                    }
                } else {
                    blockElement.setAttribute('data-pending-link-id', result.id);
                    blockElement.setAttribute('data-pending-link-slug', result.slug);
                    btn.style.background = 'rgba(25,118,210,0.8)';
                }
            }, {
                listPath: `${config.api.paths.courses}?limit=200`,
                linkPath: config.api.paths.coursesLinkBlock,
            });
        });

        blockElement.appendChild(btn);
    }

    processExistingCourseBlocks() {
        const courseBlocks = document.querySelectorAll('[data-block-name^="course-card-"][data-block-id]');

        courseBlocks.forEach(blockElement => {
            const blockName = blockElement.getAttribute('data-block-name');
            const courseId = blockElement.getAttribute('data-course-id');
            const courseStatus = blockElement.getAttribute('data-course-status') || 'draft';
            const courseSlug = blockElement.getAttribute('data-course-slug');

            // Activate link for any published block
            if (courseStatus === 'published' && courseSlug) {
                this.activateCourseLink(blockElement, courseSlug);
            }

            // ФИКС: Add status indicator - ТОЛЬКО для авторизованных на courses.html
            if (this.shouldShowStatusControls() && getAuthToken()) {
                this.updateBlockStatusIndicator(blockElement, courseStatus);
            } else {
                // Удаляем индикатор для неавторизованных
                const existingIndicator = blockElement.querySelector('.course-status-indicator');
                if (existingIndicator) {
                    existingIndicator.remove();
                }
            }

            // Add status controls if course entity exists and user is authenticated
            if (courseId && getAuthToken() && this.shouldShowStatusControls()) {
                this.addStatusControlsToBlock(blockElement, courseId);
            }

            this.addLinkButtonToBlock(blockElement);
        });
    }

    // Static method to handle course block deletion - called by block system
    static async handleCourseBlockDeletion(blockElement) {
        const courseId = blockElement.getAttribute('data-course-id');
        const blockName = blockElement.getAttribute('data-block-name');

        if (!courseId) {
            console.log(`Course block ${blockName} has no course entity to delete`);
            return false;
        }

        if (!getAuthToken()) {
            console.error('Authentication required for course deletion');
            return false;
        }

        try {
            console.log(`Deleting course entity: ${courseId} for block: ${blockName}`);

            // Call the cascading delete endpoint
            await apiDelete(`/api/courses/${courseId}`);

            console.log(`Course entity ${courseId} deleted successfully with cascade`);
            return true;

        } catch (error) {
            console.error('Error deleting course entity:', error);
            return false;
        }
    }
}

export const courseManager = new CourseManager();

// ВАЖНО: Делаем courseManager доступным глобально
window.courseManager = courseManager;

console.log('CourseManager initialized and made globally available');